import { getAPI, patchAPI, postAPI } from '@/store/api';
import { Action } from '@/store/api/actions';
import {
  InviteAdmin,
  InviteConsultant,
  InviteManager,
  InviteMarketer,
  InviteOwner
} from '@/store/models/jwt';
import {
  AssignFarm,
  ChangePassword,
  ChangeRole,
  DeleteInvitedUser,
  PaginatedInvitations,
  PaginatedUsers,
  RegisterUser,
  Role,
  SimpleUser,
  UserDetail,
  ValidateInviteToken,
  ValidateInviteTokenResponse
} from '@/store/models/user';

type InviteResponse = { message: string };

export default new (class UserAPI {
  public async getUserRoles(): Promise<Role[]> {
    return await getAPI<Role[]>(`${Action.Roles}`);
  }
  public async inviteAdmin(email: InviteAdmin[]): Promise<InviteResponse> {
    return await postAPI<InviteResponse>(Action.InviteAdmin, email);
  }
  public async enrollAdmin(user: FormData): Promise<unknown> {
    return await postAPI(Action.RegisterAdmin, user);
  }
  public async inviteOwner(email: InviteOwner[]): Promise<InviteResponse> {
    return await postAPI<InviteResponse>(Action.InviteOwner, email);
  }
  public async enrollOwner(user: FormData): Promise<unknown> {
    return await postAPI(Action.RegisterOwner, user);
  }
  public async inviteManager(email: InviteManager[]): Promise<InviteResponse> {
    return await postAPI<InviteResponse>(Action.InviteManager, email);
  }
  public async enrollManager(user: FormData): Promise<unknown> {
    return await postAPI(Action.RegisterManager, user);
  }
  public async inviteConsultant(
    email: InviteConsultant[]
  ): Promise<InviteResponse> {
    return await postAPI<InviteResponse>(Action.InviteConsultant, email);
  }
  public async enrollConsultant(user: FormData): Promise<unknown> {
    return await postAPI(Action.RegisterConsultant, user);
  }
  public async inviteMarketer(
    email: InviteMarketer[]
  ): Promise<InviteResponse> {
    return await postAPI<InviteResponse>(Action.InviteMarketer, email);
  }
  public async enrollMarketer(user: FormData): Promise<unknown> {
    return await postAPI(Action.RegisterMarketer, user);
  }
  public async validateInviteToken(
    user: ValidateInviteToken
  ): Promise<ValidateInviteTokenResponse> {
    return await postAPI<ValidateInviteTokenResponse>(
      Action.ValidateInviteToken,
      user
    );
  }
  public async registerUser(user: FormData): Promise<RegisterUser> {
    return await postAPI<RegisterUser>(Action.Register, user);
  }
  public async checkUsername(
    username: string
  ): Promise<{ exists: boolean }> {
    return await postAPI<{ exists: boolean }>(Action.UsernameExists, {
      username: username
    });
  }
  public async changePassword(
    password: ChangePassword
  ): Promise<string> {
    return await postAPI<string>(Action.SetPassword, password);
  }
  public async getUserDetailById(id: number): Promise<UserDetail> {
    return await getAPI<UserDetail>(`${Action.AdminUsers}/${id}`);
  }
  async changeRoleForUser(data: ChangeRole): Promise<UserDetail> {
    return await postAPI<UserDetail>(
      `${Action.AdminUsers}/${data.id}/change_role`,
      data
    );
  }
  async assignFarmsForUser(data: AssignFarm): Promise<UserDetail> {
    return await postAPI<UserDetail>(
      `${Action.AdminUsers}/${data.id}/assign_farms`,
      data
    );
  }
  public async getAllUsersForAdmin(query = ''): Promise<PaginatedUsers> {
    return await getAPI<PaginatedUsers>(Action.AdminUsers, query);
  }
  public async getSimpleUserList(query = ''): Promise<SimpleUser[]> {
    return await getAPI<SimpleUser[]>(Action.SimpleUserList, query);
  }

  public async getInvitations(query = ''): Promise<PaginatedInvitations> {
    return await getAPI<PaginatedInvitations>(`${Action.Invitations}`, query);
  }

  public async cancelInvitationById(id: number): Promise<unknown> {
    return await patchAPI(
      `${Action.CancelInvitation.replace('<id>', String(id))}`,
      {}
    );
  }

  public async deleteInvitedUser(data: DeleteInvitedUser): Promise<unknown> {
    return await patchAPI(
      `${Action.DeleteAdminUser.replace('<userId>', String(data.userId))}`,
      data
    );
  }

  public async activateUser(userId: number): Promise<UserDetail> {
    return await patchAPI<UserDetail>(
      `${Action.ActivateAdminUser.replace('<userId>', String(userId))}`,
      {}
    );
  }

  public async deactivateUser(userId: number): Promise<UserDetail> {
    return await patchAPI<UserDetail>(
      `${Action.DeactivateAdminUser.replace('<userId>', String(userId))}`,
      {}
    );
  }
})();
